import React, { useCallback, useEffect, useMemo, useState } from "react";
import InputDropSearch from "../../../../components/inputs/InputDropSearch";
import {
  patientsProductRelease,
  searchReleasePatients,
  unsettledDispensesByPatient,
} from "../../../../api/inventoryService";
import ReleaseProductModal from "../components/ReleaseProductModal";
import ReleaseLinesTable from "../components/ReleaseLinesTable";
import ReleasePayment from "../components/ReleasePayment";
import { linesTotal, ReleaseLine } from "../components/releaseTypes";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

function PatientRelease() {
  const [formData, setformData] = useState<FormData>({
    patientId: "",
    discount: 0,
    card: 0,
    cash: 0,
    product: [],
  });
  const [patients, setPatiens] = useState<Patients[]>([]);
  const [rxLines, setRxLines] = useState<RxLine[]>([]);

  const [open, setopen] = useState(false);

  const getPatientsData = useCallback(async (search: string) => {
    try {
      const data = await searchReleasePatients(search);
      setPatiens(data as Patients[]);
    } catch (error) {
      console.error(error);
    }
  }, []);
  useEffect(() => {
    getPatientsData("");
  }, [getPatientsData]);

  const loadUnsettledDispenses = useCallback(async (patientId: string) => {
    if (!patientId) {
      setRxLines([]);
      return;
    }
    try {
      const data = await unsettledDispensesByPatient(patientId);
      const groups = (data?.groups || []) as UnsettledGroup[];
      const lines: RxLine[] = [];
      for (const group of groups) {
        for (const line of group.lines || []) {
          const qty = Number(line.qty) || 0;
          const unitPrice = Number(line.unitPrice) || 0;
          const stock = Number(line.stock) || 0;
          const sellable = Boolean(line.linked) && qty > 0 && stock >= qty;
          const options: RxOption[] = (line.options || []).map((o: any) => ({
            productId: String(o.productId),
            productName: o.productName || "",
            scale: o.scale,
            stock: Number(o.stock) || 0,
            price: Number(o.price) || 0,
            lineTotal: Number(o.lineTotal) || 0,
          }));
          lines.push({
            prescriptionLineId: String(line.prescriptionLineId),
            treatmentId: String(line.treatmentId || group.treatmentId),
            productName: line.productName || "",
            dose: line.dose,
            doseType: line.doseType,
            qty,
            unitPrice,
            lineTotal: Number(line.lineTotal) || qty * unitPrice,
            dispense: Boolean(line.dispense),
            linked: Boolean(line.linked),
            stock,
            sellable,
            // Doctor-flagged lines come pre-ticked when they can actually sell
            included: sellable && Boolean(line.dispense),
            options,
            // Server sends the default pick in the flat fields
            productId: line.productId ? String(line.productId) : "",
          });
        }
      }
      setRxLines(lines);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load prescription lines for this patient.");
    }
  }, []);

  /** Switch the brand filling a line; price, stock and totals follow it. */
  const chooseRxBrand = useCallback(
    (prescriptionLineId: string, productId: string) => {
      setRxLines((prev) =>
        prev.map((line) => {
          if (line.prescriptionLineId !== prescriptionLineId) return line;
          const option = line.options.find((o) => o.productId === productId);
          if (!option) return line;
          const sellable = line.qty > 0 && option.stock >= line.qty;
          return {
            ...line,
            productId: option.productId,
            productName: option.productName,
            unitPrice: option.price,
            lineTotal: option.lineTotal,
            stock: option.stock,
            sellable,
            included: sellable && line.included,
          };
        })
      );
    },
    []
  );

  const toggleRxLine = useCallback((prescriptionLineId: string) => {
    setRxLines((prev) =>
      prev.map((line) =>
        line.prescriptionLineId === prescriptionLineId && line.sellable
          ? { ...line, included: !line.included }
          : line
      )
    );
  }, []);

  const includedRx = useMemo(
    () => rxLines.filter((l) => l.included),
    [rxLines]
  );
  const rxTotal = useMemo(
    () => includedRx.reduce((sum, l) => sum + l.lineTotal, 0),
    [includedRx]
  );
  const manualTotal = useMemo(
    () => linesTotal(formData.product),
    [formData.product]
  );
  const total = rxTotal + manualTotal;
  const netTotal = total - (Number(formData.discount) || 0);

  const handleChange = useCallback(
    (e: any) => {
      const { name, value } = e.target;
      setformData((prevData) => ({ ...prevData, [name]: value }));
      if (name === "patientId") {
        void loadUnsettledDispenses(value);
      }
    },
    [loadUnsettledDispenses]
  );
  const searchSupplier = useCallback(
    (e: any) => {
      getPatientsData(e);
    },
    [getPatientsData]
  );
  const openModal = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      setopen(true);
    },
    []
  );
  const closeModal = useCallback(() => {
    setopen(false);
  }, []);
  const addProduct = useCallback(
    (product: ReleaseLine) => {
      setformData((prevFormData) => ({
        ...prevFormData,
        product: [...prevFormData.product, product],
      }));
      closeModal();
    },
    [closeModal]
  );
  const removeProductById = useCallback((index: number) => {
    setformData((prevFormData) => ({
      ...prevFormData,
      product: prevFormData.product.filter((_product, i) => i !== index),
    }));
  }, []);
  const navigate = useNavigate();

  const handleSubmit = (e: any) => {
    e.preventDefault();

    if (!formData.patientId) {
      toast.error("Choose a patient first.");
      return;
    }
    if (includedRx.length === 0 && formData.product.length === 0) {
      toast.error("Add a product or select a prescription line first.");
      return;
    }

    const payload = {
      patientId: formData.patientId,
      discount: Number(formData.discount) || 0,
      card: Number(formData.card) || 0,
      cash: Number(formData.cash) || 0,
      total,
      netTotal,
      product: [
        ...includedRx.map((line) => ({
          fromPrescription: true,
          prescriptionLineId: line.prescriptionLineId,
          treatmentId: line.treatmentId,
          quantity: line.qty,
          productId: line.productId,
        })),
        ...formData.product,
      ],
    };

    patientsProductRelease(payload)
      .then(() => {
        navigate("/client/inventory/release/all-release?type=patient");
      })
      .catch((error) => {
        console.error(error?.response);
        toast.error(
          error?.response?.data?.message || "Failed to complete payment."
        );
      });
  };
  return (
    <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 overflow-hidden">
      {open && (
        <ReleaseProductModal
          title="Add Product to Patient"
          releaseType="patient"
          addProduct={addProduct}
          closeModal={closeModal}
        />
      )}

      <form onSubmit={handleSubmit} className="p-3 space-y-3">
        <InputDropSearch
          data={patients}
          name="patientId"
          label="Patient"
          selectName1="fullName"
          selectName2="regNo"
          selectValue="_id"
          onChange={handleChange}
          onSearch={searchSupplier}
        />

        {formData.patientId && rxLines.length > 0 && (
          <div className="border border-teal-200 dark:border-teal-800 rounded-lg overflow-hidden">
            <div className="px-2.5 py-1.5 bg-teal-50 dark:bg-teal-900/30">
              <p className="text-[11px] font-semibold text-teal-700 dark:text-teal-300">
                Prescribed medicines — checked against inventory just now
              </p>
            </div>
            <table className="w-full text-xs">
              <tbody className="divide-y divide-slate-100 dark:divide-slate-600">
                {rxLines.map((line) => (
                  <tr
                    key={line.prescriptionLineId}
                    className={
                      line.included
                        ? "bg-teal-50/60 dark:bg-teal-900/20"
                        : !line.sellable
                          ? "opacity-60"
                          : ""
                    }
                  >
                    <td className="px-2.5 py-1.5 w-8">
                      <input
                        type="checkbox"
                        checked={line.included}
                        disabled={!line.sellable}
                        onChange={() => toggleRxLine(line.prescriptionLineId)}
                        className="w-3.5 h-3.5 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                      />
                    </td>
                    <td className="px-2.5 py-1.5 font-medium text-xs text-slate-700 dark:text-slate-200">
                      {line.options.length > 1 ? (
                        <select
                          value={line.productId}
                          onChange={(e) =>
                            chooseRxBrand(
                              line.prescriptionLineId,
                              e.target.value
                            )
                          }
                          className="w-full max-w-[13rem] rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 px-1.5 py-1 text-xs text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-teal-500"
                          title="Several brands fill this dose"
                        >
                          {line.options.map((o) => (
                            <option key={o.productId} value={o.productId}>
                              {o.productName} · Rs {o.price}
                              {o.stock < line.qty ? ` · stock ${o.stock}` : ""}
                            </option>
                          ))}
                        </select>
                      ) : (
                        line.productName
                      )}
                      {line.dispense && (
                        <span className="ml-1 text-[10px] text-teal-600 dark:text-teal-400">
                          (Dr. requested)
                        </span>
                      )}
                    </td>
                    <td className="px-2.5 py-1.5 text-xs text-slate-600 dark:text-slate-300">
                      {line.qty} × {line.unitPrice}
                    </td>
                    <td className="px-2.5 py-1.5 text-xs">
                      {!line.linked ? (
                        <span className="text-amber-600 dark:text-amber-400">
                          awaiting product link
                        </span>
                      ) : line.stock < line.qty ? (
                        <span className="text-red-500 dark:text-red-400">
                          stock {line.stock} of {line.qty}
                        </span>
                      ) : (
                        <span className="text-slate-500 dark:text-slate-400">
                          stock {line.stock}
                        </span>
                      )}
                    </td>
                    <td className="px-2.5 py-1.5 font-semibold text-xs text-emerald-600 dark:text-emerald-400">
                      {line.sellable ? line.lineTotal : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <button
          type="button"
          onClick={(e: any) => openModal(e)}
          className="w-full px-3 py-2 rounded-lg border-2 border-dashed border-primary/30 dark:border-primary/50 text-primary font-medium hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors flex items-center justify-center gap-1.5 text-xs"
        >
          <span className="text-base">+</span> Add Product
        </button>

        <ReleaseLinesTable lines={formData.product} onRemove={removeProductById} />

        <ReleasePayment
          total={total}
          netTotal={netTotal}
          discount={formData.discount}
          card={formData.card}
          cash={formData.cash}
          onChange={handleChange}
        />

        <button
          type="submit"
          className="w-full px-4 py-2.5 rounded-lg bg-gradient-to-r from-primary to-primaryLight text-white font-semibold hover:shadow-lg hover:shadow-primary/20 transition-all text-xs"
        >
          Complete Payment
        </button>
      </form>
    </div>
  );
}

export default PatientRelease;
interface Patients {
  _id: string;
  fullName: string;
  regNo: string;
}
/** One brand that can fill a prescribed dose. */
interface RxOption {
  productId: string;
  productName: string;
  scale?: string;
  stock: number;
  price: number;
  lineTotal: number;
}

interface RxLine {
  prescriptionLineId: string;
  treatmentId: string;
  productName: string;
  dose?: string;
  doseType?: string;
  qty: number;
  unitPrice: number;
  lineTotal: number;
  dispense: boolean;
  linked: boolean;
  stock: number;
  sellable: boolean;
  included: boolean;
  options: RxOption[];
  productId: string;
}

interface UnsettledLine {
  options?: any[];
  prescriptionLineId: string;
  treatmentId: string;
  productId?: string | null;
  productName?: string;
  dose?: string;
  doseType?: string;
  qty?: number;
  unitPrice?: number;
  lineTotal?: number;
  dispense?: boolean;
  linked?: boolean;
  stock?: number;
}

interface UnsettledGroup {
  treatmentId: string;
  lines: UnsettledLine[];
}

interface FormData {
  patientId: string;
  product: ReleaseLine[];
  discount: number;
  card: number;
  cash: number;
}
